import { useState } from "react";
import { Link } from "react-router-dom";
// Hooks
import { useLogin } from "../../../hooks/useLogin";
// Styles
import "../../../styles/pages/Login.css";

function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { login, error, isPending } = useLogin();

  // Submit the user's credentials to Firebase
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent duplicate submissions while a login request is running
    if (isPending) return;

    await login(email.trim(), password);
  };

  return (
    <main className="login-page position-relative d-flex align-items-center overflow-hidden">
      {/* Subtle background grid */}
      <div className="login-grid position-absolute top-0 start-0 w-100 h-100" aria-hidden="true" />
      {/* Decorative Benoma background text */}
      <div className="login-background-text position-absolute user-select-none" aria-hidden="true">
        BENOMA
      </div>
      <div className="container position-relative z-1">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6 col-xl-5">
            {/* Back to the main website */}
            <Link
              to="/"
              className="login-back-link d-inline-flex align-items-center gap-2 text-decoration-none mb-4"
            >
              <i className="bi bi-arrow-left" aria-hidden="true" />
              Back to Benoma
            </Link>
            <div className="login-card">
              {/* Login header */}
              <div className="login-card-header">
                <div className="d-flex align-items-center justify-content-between gap-3 mb-4">
                  <span className="login-label font-monospace text-danger">MEMBER ACCESS</span>
                  <span className="login-status d-flex align-items-center gap-2 font-monospace">
                    <span className="login-status-dot rounded-circle" />
                    secure
                  </span>
                </div>
                <h1 className="login-title fw-bold mb-3">Welcome back.</h1>
                <p className="login-description text-secondary mb-0">
                  Sign in to manage projects and your Benoma profile.
                </p>
              </div>
              {/* Login form */}
              <div className="login-card-body">
                {error && (
                  <div className="login-error d-flex align-items-start gap-3 mb-4" role="alert">
                    <i className="bi bi-exclamation-circle text-danger" aria-hidden="true" />
                    <div>
                      <span className="d-block fw-semibold mb-1">Unable to sign in</span>
                      <small>{error}</small>
                    </div>
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                  {/* Email */}
                  <div className="mb-4">
                    <label htmlFor="email" className="login-form-label form-label">
                      Email address
                    </label>
                    <div className="login-input-wrapper position-relative">
                      <i
                        className="bi bi-envelope login-input-icon position-absolute"
                        aria-hidden="true"
                      />
                      <input
                        type="email"
                        id="email"
                        className="form-control login-input"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        inputMode="email"
                        autoCapitalize="none"
                        spellCheck="false"
                        disabled={isPending}
                        required
                      />
                    </div>
                  </div>
                  {/* Password */}
                  <div className="mb-2">
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <label htmlFor="password" className="login-form-label form-label">
                        Password
                      </label>
                      <Link
                        to="/forgot-password"
                        className="login-forgot-link text-decoration-none"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <div className="login-input-wrapper position-relative">
                      <i
                        className="bi bi-lock login-input-icon position-absolute"
                        aria-hidden="true"
                      />
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        className="form-control login-input login-password-input"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        disabled={isPending}
                        required
                      />
                      {/* Show or hide password */}
                      <button
                        type="button"
                        className="login-password-toggle position-absolute"
                        onClick={() => setShowPassword((current) => !current)}
                        disabled={isPending}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        aria-pressed={showPassword}
                      >
                        <i
                          className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>
                  {/* Submit login form */}
                  <button
                    type="submit"
                    className="btn btn-danger login-submit w-100 d-flex align-items-center justify-content-center gap-2 mt-4"
                    disabled={isPending}
                  >
                    {isPending ? (
                      <>
                        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                        <span>Signing in...</span>
                      </>
                    ) : (
                      <>
                        <span>Sign in</span>
                        <i className="bi bi-arrow-right" aria-hidden="true" />
                      </>
                    )}
                  </button>
                </form>
              </div>
              {/* Terminal-style footer */}
              <div className="login-card-footer font-monospace">
                <span className="text-danger">›</span>
                <span className="text-secondary"> authentication required</span>
                <span className="login-terminal-cursor" aria-hidden="true">
                  _
                </span>
              </div>
            </div>
            {/* Portal label */}
            <p className="login-footer-text text-center text-secondary font-monospace mt-4 mb-0">
              BENOMA / MEMBER PORTAL
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}

export default LoginForm;
